import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useLocalStorage } from '@utils/utils';
import { CalendarViewType } from './calendarView';

interface RampResource {
  id: string;
  title: string;
}

interface Params {
  calendarRef: React.MutableRefObject<any>;
  rampsResources: RampResource[];
  currentDate: string;
  setCurrentDate: (date: string) => void;
  currentView: CalendarViewType;
  setCurrentView: (view: CalendarViewType) => void;
  detailRampViewId: string | null;
  setDetailRampViewId: (id: string | null) => void;
}

/**
 * Column-width / horizontal-paging maths and the day ↔ week ↔ work-week switching, all of which
 * poke at FullCalendar's DOM directly. Extracted from the calendar shell unchanged.
 */
export const useCalendarLayout = ({
  calendarRef,
  rampsResources,
  currentDate,
  setCurrentDate,
  currentView,
  setCurrentView,
  detailRampViewId,
  setDetailRampViewId,
}: Params) => {
  const [dayMinWidth, setDayMinWidth] = useState<number | undefined>(undefined);
  const [calendarStep, setCalendarStep] = useState(1);
  const [visibleRampsCount, setVisibleRampsCount] = useLocalStorage<number | undefined>('CALENDAR_COLS_COUNT', undefined);
  const [showLargeView, setShowLargeView] = useLocalStorage('TIMESLOT_CALENDAR_SHOW_LARGE_VIEW', false);
  const [nextRampDisabled, setNextRampDisabled] = useState(false);
  const [prevRampDisabled, setPrevRampDisabled] = useState(false);
  const [lastCurrentDate, setLastCurrentDate] = useState<string | null>(null);

  const resetCalendarShift = () => {
    const scrollHeaderEl = document.querySelector('.fc-col-header') as HTMLElement | null;
    const scrollEl = document.querySelector('.fc-timegrid-body') as HTMLElement | null;

    if (!scrollEl || !scrollHeaderEl) {
      setCalendarStep(1);
      return;
    }

    scrollEl.style.transform = 'translateX(0)';
    scrollHeaderEl.style.transform = 'translateX(0)';
    setCalendarStep(1);
  };

  /**
   * Move the calendar to a date. Only state is written here: `currentDate` is the single source of
   * truth and the effect below navigates FullCalendar to match. Doing it the other way round (call
   * `gotoDate` and wait for the `datesSet` callback to set the state) silently does nothing whenever
   * the ref isn't attached yet, and leaves `initialDate` — read once, at mount — as the only link.
   */
  const changeDateCal = (date: string) => {
    setCurrentDate(date);
    resetCalendarShift();
  };

  // Keep the grid on whatever day the state says, wherever the change came from (stepper, picker,
  // view switch). Guarded by an is-same-day check so it doesn't fight FullCalendar's own datesSet.
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api || !currentDate) return;
    if (!dayjs(api.getDate()).isSame(dayjs(currentDate), 'day')) {
      api.gotoDate(dayjs(currentDate).toDate());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const handleMoveClick = useCallback(
    (direction: 'left' | 'right') => {
      const scrollHeaderEl = document.querySelector('.fc-col-header') as HTMLElement | null;
      const scrollEl = document.querySelector('.fc-timegrid-body') as HTMLElement | null;
      const rampsCount = rampsResources.length;

      if (!visibleRampsCount || !scrollEl || !scrollHeaderEl) return;

      const maxSteps = Math.max(1, rampsCount - visibleRampsCount + 1);

      let newStep = calendarStep;
      if (direction === 'right') {
        newStep = Math.min(maxSteps, calendarStep + 1);
      } else {
        newStep = Math.max(1, calendarStep - 1);
      }

      const transformValue = dayMinWidth * (newStep - 1);

      scrollEl.style.transform = `translateX(-${transformValue}px)`;
      scrollHeaderEl.style.transform = `translateX(-${transformValue}px)`;

      setCalendarStep(newStep);
      setPrevRampDisabled(newStep <= 1);
      setNextRampDisabled(newStep >= maxSteps);
    },
    [calendarStep, visibleRampsCount, dayMinWidth, rampsResources.length],
  );

  const restrictCalendarCols = useCallback(
    (count?: number) => {
      const rampsLength = rampsResources.length;
      // reset calendar scroll position
      resetCalendarShift();

      // if it makes sense, set column widths
      if (count && rampsLength > count) {
        // set column width
        const wrapperWidth = Math.round(calendarRef.current.elRef.current.getBoundingClientRect().width);
        setDayMinWidth(wrapperWidth / count);
      } else {
        // reset column width
        setDayMinWidth(undefined);
      }
    },
    [rampsResources],
  );

  const handleViewToggle = (type: 'day' | 'week' | 'workWeek', count?: number) => {
    if (type === 'week') {
      // set view to week
      setCurrentView('timeGridWeek');
      calendarRef.current.getApi().changeView('timeGridWeek');

      // restrict results to only one resource
      setDetailRampViewId(rampsResources[calendarStep - 1]?.id);

      // set number of calendar columns
      restrictCalendarCols(7);

      if (lastCurrentDate) {
        changeDateCal(lastCurrentDate);
      }
    } else if (type === 'workWeek') {
      // set view to work week
      setCurrentView('timeGridWorkWeek');
      calendarRef.current.getApi().changeView('timeGridWorkWeek');

      // restrict results to only one resource
      setDetailRampViewId(rampsResources[calendarStep - 1]?.id);

      // set number of calendar columns
      restrictCalendarCols(5);

      changeDateCal(dayjs(currentDate).startOf('isoWeek').format());
    } else if (type === 'day') {
      // remove resource restriction
      setDetailRampViewId(null);

      // set view to day
      setCurrentView('resourceTimeGridDay');
      calendarRef.current.getApi().changeView('resourceTimeGridDay');

      if (count) {
        restrictCalendarCols(count);
      }

      if (lastCurrentDate) {
        changeDateCal(lastCurrentDate);
      }
    }
  };

  /**
   * Week views show a single ramp at a time. `initial` resolves which one that is (and the
   * prev/next availability); otherwise it steps to the neighbouring ramp.
   */
  const changeWeekViewRamp = useCallback(
    (initial: boolean, direction?: 'next' | 'prev') => {
      if (currentView === 'resourceTimeGridDay') {
        return;
      }

      const fallbackIndex = Math.max(0, Math.min((calendarStep || 1) - 1, rampsResources.length - 1));
      const effectiveCurrentId = detailRampViewId ?? rampsResources[fallbackIndex]?.id ?? rampsResources[0]?.id;
      const currentIndex = rampsResources.findIndex(r => r.id === effectiveCurrentId);

      const currentId = rampsResources[currentIndex]?.id;
      const prevTargetId = rampsResources[currentIndex - 1]?.id;
      const nextTargetId = rampsResources[currentIndex + 1]?.id;
      const futurePrevTargetId = rampsResources[currentIndex - 2]?.id;
      const futureNextTargetId = rampsResources[currentIndex + 2]?.id;

      if (initial) {
        setDetailRampViewId(currentId);
        setNextRampDisabled(!nextTargetId);
        setPrevRampDisabled(!prevTargetId);
      } else {
        if (prevTargetId && direction === 'prev') {
          setDetailRampViewId(prevTargetId);
        }

        if (nextTargetId && direction === 'next') {
          setDetailRampViewId(nextTargetId);
        }

        setNextRampDisabled(!futureNextTargetId);
        setPrevRampDisabled(!futurePrevTargetId);
      }
    },
    [rampsResources, detailRampViewId, currentView, calendarStep],
  );

  /** Week/work-week are only selectable when the day view shows all ramps (or exactly one). */
  const weekDisabled = visibleRampsCount === undefined || (visibleRampsCount > 1 && visibleRampsCount < 7);

  return {
    dayMinWidth,
    calendarStep,
    visibleRampsCount,
    setVisibleRampsCount,
    showLargeView,
    setShowLargeView,
    nextRampDisabled,
    prevRampDisabled,
    lastCurrentDate,
    setLastCurrentDate,
    weekDisabled,
    resetCalendarShift,
    changeDateCal,
    handleMoveClick,
    restrictCalendarCols,
    handleViewToggle,
    changeWeekViewRamp,
  };
};